import * as React from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { capitalize } from "@mui/material/utils";
import withStyles from "@mui/styles/withStyles";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";

export const styles = ({ startIcon, endIcon, pendingPosition }) => {
  const hasIcon = !!startIcon || !!endIcon;
  return {
    /* Styles applied to the root element. */
    root: {},
    /* Styles applied to the root element if `pending={true}`. */
    pending: {},
    /* Styles applied to the pendingIndicator element. */
    pendingIndicator: {
      position: hasIcon ? "absolute" : "unset",
      visibility: "visible",
      display: "flex"
    },
    /* Styles applied to the pendingIndicator element if `pendingPosition="center"`. */
    pendingIndicatorCenter: {
      left: "50%",
      transform: "translate(-50%)"
    },
    /* Styles applied to the pendingIndicator element if `pendingPosition="start"`. */
    pendingIndicatorStart: {
      paddingRight: hasIcon ? 0 : 8,
      marginLeft: hasIcon ? 0 : -4,
      left: 14
    },
    /* Styles applied to the pendingIndicator element if `pendingPosition="end"`. */
    pendingIndicatorEnd: {
      right: 14
    },
    /* Styles applied to the endIcon element if `pending={true}` and `pendingPosition="end"`. */
    endIconPendingEnd: {
      visibility: "hidden"
    },
    /* Styles applied to the startIcon element if `pending={true}` and `pendingPosition="start"`. */
    startIconPendingStart: {
      visibility: "hidden"
    },
    /* Styles applied to the label element if `pending={true}` and `pendingPosition="center"`. */
    labelPendingCenter: {
      visibility: "hidden"
    }
  };
};

const PendingIndicator = <CircularProgress color="inherit" size={16} />;

/**
 * TODO: After material-ui is upgraded to v5, this component can be removed
 * in favor of material-ui's loading button component
 */
const LoadingButton = React.forwardRef(function LoadingButton(props, ref) {
  const {
    children,
    classes,
    className,
    disabled = false,
    pending = false,
    pendingIndicator = PendingIndicator,
    pendingPosition = "center",
    ...other
  } = props;

  return (
    <Button
      className={clsx(
        classes.root,
        {
          [classes.pending]: pending
        },
        className
      )}
      disabled={disabled || pending}
      ref={ref}
      classes={{
        startIcon:
          classes[
            `startIcon${pending ? "Pending" : ""}${capitalize(pendingPosition)}`
          ],
        endIcon:
          classes[
            `endIcon${pending ? "Pending" : ""}${capitalize(pendingPosition)}`
          ],
        label:
          classes[
            `label${pending ? "Pending" : ""}${capitalize(pendingPosition)}`
          ]
      }}
      {...other}
    >
      {pending && (
        <div
          className={clsx(
            classes.pendingIndicator,
            classes[`pendingIndicator${capitalize(pendingPosition)}`]
          )}
        >
          {pendingIndicator}
        </div>
      )}

      {children}
    </Button>
  );
});

LoadingButton.propTypes = {
  /**
   * The content of the component.
   */
  children: PropTypes.node,
  /**
   * Override or extend the styles applied to the component.
   */
  classes: PropTypes.object,
  /**
   * @ignore
   */
  className: PropTypes.string,
  /**
   * If `true`, the component is disabled.
   * @default false
   */
  disabled: PropTypes.bool,
  /**
   * If `true`, the pending indicator is shown.
   * @default false
   */
  pending: PropTypes.bool,
  /**
   * Element placed before the children if the button is in pending state.
   * @default <CircularProgress color="inherit" size={16} />
   */
  pendingIndicator: PropTypes.node,
  /**
   * The pending indicator can be positioned on the start, end, or the center of the button.
   * @default 'start'
   */
  pendingPosition: PropTypes.oneOf(["start", "end", "center"])
};

LoadingButton.defaultProps = {
  pendingPosition: "start",
  pendingIndicator: <CircularProgress color="inherit" size={16} />,
  pending: false,
  disabled: false
};

export default withStyles(styles, { name: "MuiLoadingButton" })(LoadingButton);
